import React from 'react';

import { IconArrowUpward, IconTick } from '@xingternal/icons';
import { Marker, MarkerVariant } from '@xingternal/marker';

import {
  JobKeyfactV2Fragment,
  JobTeaserVisibleJobFragment as RecommendationType,
  SalaryEstimate as SalaryEstimateType,
  SalaryRange as SalaryRangeType,
  Salary as SalaryType,
  SimilarJobVisibleJobFragment as SimilarJobType,
} from '@/graphql/generated';
import { isValidSalary, isValidSalaryEstimate, isValidSalaryRange } from '@/helpers/salary-utils';

import { Salary } from '../salary-metadata';

const NOT_BOOKMARKED = 'NOT_BOOKMARKED';

type JobData = RecommendationType | SimilarJobType;

// eslint-disable-next-line
export const selectJobData = (job: JobData) => {
  const { companyInfo, salary, date, title, location } = job;

  const companyName = companyInfo?.companyNameOverride ?? '';
  const companyLogos = companyInfo?.company?.logos ?? {};
  const ratingCount = companyInfo?.company?.kununuData?.ratingCount ?? 0;
  const ratingAverage = companyInfo?.company?.kununuData?.ratingAverage ?? 0;
  const employmentTypeValue = (job as RecommendationType).employmentType?.localizationValue;
  const bookmarkState = (job as RecommendationType).userInteractions?.bookmark?.state ?? NOT_BOOKMARKED;
  const isBookmarked = bookmarkState !== NOT_BOOKMARKED;

  const showSalary =
    typeof (salary as SalaryType)?.amount === 'number' ||
    (typeof (salary as SalaryRangeType)?.minimum === 'number' &&
      typeof (salary as SalaryRangeType)?.maximum === 'number');

  const showRating = ratingCount > 0 && ratingAverage >= 0;

  return {
    salary,
    showSalary,
    date,
    title,
    location,
    companyName,
    companyLogos,
    ratingAverage,
    showRating,
    employmentTypeValue,
    isBookmarked,
  };
};

export type SalaryVariant = SalaryType | SalaryRangeType | SalaryEstimateType;

export const hasEmploymentTypeFact = (facts: JobKeyfactV2Fragment[]): boolean =>
  facts.findIndex(
    (fact) =>
      fact.__typename === 'JobMatchingHighlightsJobKeyfactStringV2' && fact.type === 'job_keyfact_employment_type'
  ) !== -1;

export const hasSalaryFact = (facts: JobKeyfactV2Fragment[]): boolean =>
  facts.findIndex((fact) => fact.__typename === 'JobMatchingHighlightsJobKeyfactSalaryV2') !== -1;

type FactMarkerProps = {
  variant: MarkerVariant;
  fact: JobKeyfactV2Fragment;
};

export const FactMarker: React.FC<FactMarkerProps> = ({ variant, fact }) => {
  const withIcon = variant !== 'default';

  if (fact.__typename === 'JobMatchingHighlightsJobKeyfactSalaryV2') {
    const salary = fact.value;
    if (!(isValidSalary(salary) || isValidSalaryRange(salary) || isValidSalaryEstimate(salary))) {
      return null;
    }

    return (
      <Marker icon={withIcon ? IconArrowUpward : undefined} variant={variant}>
        <Salary salary={salary} />
      </Marker>
    );
  }

  return (
    <Marker icon={withIcon ? IconTick : undefined} variant={variant}>
      {fact.localization.localizationValue}
    </Marker>
  );
};
